import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalFooter,
} from "@/components/ui";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { formatReaderSerialNumber } from "@/lib/readerSerialNumber";
import { readerService } from "@/services/readerService";
import type { ReaderInventory } from "@/types";

interface PairReaderModalProps {
  isOpen: boolean;
  reader: ReaderInventory | null;
  onClose: () => void;
  onPaired: () => Promise<void>;
}

export function PairReaderModal({
  isOpen,
  reader,
  onClose,
  onPaired,
}: PairReaderModalProps) {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const [pairingCode, setPairingCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPairingCode("");
    setError(null);
  }, [isOpen, reader?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reader) return;
    const code = pairingCode.trim().toUpperCase();
    if (code.length < 8 || code.length > 9) {
      setError(t("pairModal.codeInvalid"));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await readerService.pairSumup(reader.id, { pairing_code: code });
      toast.success(t("page.readerPaired"));
      await onPaired();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "readerOperation"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen && reader != null}
      onClose={onClose}
      title={t("pairModal.title")}
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="pair-reader-form"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("pairModal.submit")}
          </Button>
        </ModalFooter>
      }
    >
      {reader ? (
        <form
          id="pair-reader-form"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <p className="text-sm text-surface-600">
            {t("pairModal.help", {
              serial: formatReaderSerialNumber(
                reader.serial_number,
                reader.provider,
              ),
            })}
          </p>
          <div>
            <Label htmlFor="pairingCode">{t("pairModal.codeLabel")}</Label>
            <Input
              id="pairingCode"
              value={pairingCode}
              onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
              placeholder={t("pairModal.codePlaceholder")}
              maxLength={9}
              autoComplete="off"
              disabled={isLoading}
              className="mt-2"
            />
          </div>
          {error ? <Alert variant="error">{error}</Alert> : null}
        </form>
      ) : null}
    </Modal>
  );
}
